import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

// Import all SVG icons as React components
import { ReactComponent as ArrowIcon } from "../../assets/icons/svg/arrow-twoside-icon.svg";
import { ReactComponent as PenIcon } from "../../assets/icons/svg/pen-icon.svg";
import { ReactComponent as DownloadIcon } from "../../assets/icons/svg/download-icon.svg";
import { ReactComponent as ArrowReloadIcon } from "../../assets/icons/svg/arrow-reload-icon.svg";
import { ReactComponent as PencilEditIcon } from "../../assets/icons/svg/pencil-edit-icon.svg";
import { ReactComponent as PlayIcon } from "../../assets/icons/svg/play-icon.svg";
import { ReactComponent as PauseIcon } from "../../assets/icons/svg/pause-icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/svg/delete-icon.svg";
import { ReactComponent as CalenderIcon } from "../../assets/icons/svg/calender-icon.svg";
import { ReactComponent as DesktopPotrateIcon } from "../../assets/icons/svg/desktop-potrate-icon.svg";
import { ReactComponent as TagIcon } from "../../assets/icons/svg/tag-icon.svg";
import { ReactComponent as WifiLiveIcon } from "../../assets/icons/svg/wifi-live-icon.svg";
import { ReactComponent as WifiOfflineIcon } from "../../assets/icons/svg/wifi-offline-icon.svg";

// Import components for popups
import DeletePopup from "./DeletePopup";
import ItemListPopup from "./ItemListPopup";
import CustomButton from "./CommonButton";
import ToastContainer, { useToast } from "./Toast_Message";

import { useCreatives } from "../../context/CreativeContext";
import ScheduledCreativePreview from "../screens/Scheduled/scheduledScreens/ScheduledCreativePreview";

interface ScheduledCreative {
  id: string;
  title: string;
  imageUrl: string;
  orientation: "landscape" | "portrait";
  format: string;
  createdOn: string;
  dateRange: string;
  locations: string;
  tags: string[];
  isPlaying: boolean;
  scheduleType: "now" | "later" | "selection";
  announcement: "general" | "tag-level";
  scheduleDetails: any;
  fileFormat?: string;
}

/**
 * Props interface for the ScheduledCreativesCard component
 */
interface ScheduledCreativesCardProps {
  creative: ScheduledCreative;
  onUpdate?: (updatedCreative: ScheduledCreative) => void;
  onDelete?: (creativeId: string) => void;
  allCreatives?: ScheduledCreative[];
}

/**
 * ScheduledCreativesCard Component
 * Displays scheduled creative data from ScheduleCreativePage
 * Integrated with ScheduledCreativesListPage for data management
 * Now synced with CreativesCard through global context
 */
const ScheduledCreativesCard: React.FC<ScheduledCreativesCardProps> = ({
  creative,
  onUpdate,
  onDelete,
  allCreatives = [],
}) => {
  const { updateCreativeGlobally, deleteCreativeGlobally } = useCreatives();
  const navigate = useNavigate();
  const { showToast, toasts } = useToast();

  const [imageError, setImageError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(creative.title);
  const [titleError, setTitleError] = useState("");
  const [showActionConfirm, setShowActionConfirm] = useState(false);
  const [actionType, setActionType] = useState<"pause" | "delete">("delete");
  const [showPreview, setShowPreview] = useState(false);

  /**
   * Get dimension based on orientation
   */
  const getDimensions = (orientation: string) => {
    return orientation === "landscape" ? "1920 × 1080" : "1080 × 1920";
  };

  /**
   * Validate title for duplicates and empty input
   */
  const validateTitle = (title: string): string => {
    if (!title.trim()) {
      return "Title cannot be empty";
    }

    const creativesToCheck = allCreatives;
    const isDuplicate = creativesToCheck.some(
      (c: ScheduledCreative) =>
        c.id !== creative.id &&
        c.title.toLowerCase().trim() === title.toLowerCase().trim()
    );

    if (isDuplicate) {
      return "A creative with this title already exists";
    }

    return "";
  };

  /**
   * Handle title change with validation
   */
  const handleTitleChange = (newTitle: string) => {
    setEditedTitle(newTitle);
    setTitleError("");
  };

  /**
   * Toggles the play/pause state using global context
   */
  const handleTogglePlayPause = (): void => {
    if (creative.isPlaying) {
      setActionType("pause");
      setShowActionConfirm(true);
    } else {
      const updatedCreative = { ...creative, isPlaying: true };
      updateCreativeGlobally(creative.id, { isPlaying: true });
      onUpdate?.(updatedCreative);
      showToast("success", "", `Creative activated successfully!`);
    }
  };

  /**
   * Handles creative deletion with confirmation
   */
  const handleDeleteClick = (): void => {
    setActionType("delete");
    setShowActionConfirm(true);
  };

  /**
   * Handles the actual deletion
   */
  const handleDelete = (): void => {
    deleteCreativeGlobally(creative.id);
    onDelete?.(creative.id);
    showToast("success", "", "Creative deleted successfully!");
  };

  /**
   * Handles download functionality
   */
  const handleDownload = (): void => {
    try {
      const link = document.createElement("a");
      link.href = creative.imageUrl;
      link.download = creative.title || "creative-image";

      if (
        creative.imageUrl.startsWith("data:") ||
        creative.imageUrl.startsWith("blob:")
      ) {
        link.setAttribute(
          "download",
          `${creative.title || "creative-image"}.png`
        );
      }

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showToast("success", "", "Creative downloaded successfully!");
    } catch (error) {
      showToast("error", "", "Failed to download creative!");
      console.error("Download error:", error);
    }
  };

  /**
   * Handles image error
   */
  const handleImageError = (): void => {
    setImageError(true);
  };

  /**
   * Handles preview popup
   */
  const handlePreviewClick = (): void => {
    setShowPreview(true);
  };

  /**
   * Handles replace functionality - navigates to replace screen
   * Pass the current location as state so ReplaceCreativeScreen knows where to navigate back
   * Updated to work with both live and offline creatives
   */
  const handleReplaceClick = (): void => {
    console.log("Replace button clicked!", creative.id); // Add this for debugging
    console.log("Current pathname:", window.location.pathname); // Debug current path

    navigate(`/scheduled/replace/${creative.id}`, {
      state: {
        from: window.location.pathname,
        creativeType: "scheduled",
        originalCreative: creative,
      },
    });

    console.log("Navigate called"); // Confirm navigation was called
  };

  /**
   * Handles edit functionality
   */
  const handleEditClick = (): void => {
    setIsEditing(true);
    setEditedTitle(creative.title);
    setTitleError("");
  };

  /**
   * Handles title update with validation
   */
  const handleTitleUpdate = (): void => {
    const error = validateTitle(editedTitle);
    setTitleError(error);
    if (error) {
      return;
    }

    if (editedTitle !== creative.title) {
      updateCreativeGlobally(creative.id, { title: editedTitle });
      const updatedCreative = { ...creative, title: editedTitle };
      onUpdate?.(updatedCreative);
      showToast("success", "", "Title updated successfully!");
      setIsEditing(false);
    } else {
      setIsEditing(false);
    }
  };

  /**
   * Handles the confirmation popup actions
   */
  const handleConfirmAction = (): void => {
    if (actionType === "pause") {
      updateCreativeGlobally(creative.id, { isPlaying: false });
      const updatedCreative = { ...creative, isPlaying: false };
      onUpdate?.(updatedCreative);
      showToast("success", "", "Creative deactivated successfully!");
    } else {
      handleDelete();
    }
    setShowActionConfirm(false);
  };

  /**
   * Format the creation date for display
   */
  const formatCreatedOn = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  /**
   * Get status display information
   */
  const getStatusInfo = () => {
    if (creative.isPlaying) {
      return {
        text: "Live",
        bgColor: "bg-green-100",
        textColor: "text-green-700",
        icon: <WifiLiveIcon width={12} height={12} />,
      };
    } else {
      return {
        text: "Offline",
        bgColor: "bg-red-100",
        textColor: "text-red-700",
        icon: <WifiOfflineIcon width={12} height={12} />,
      };
    }
  };

  /**
   * Get action button configuration based on creative state
   * Updated to show replace button for both live and offline creatives
   * Delete button only shows for offline creatives
   */
  const getActionButtonConfig = () => {
    if (creative.isPlaying) {
      return {
        icon: (
          <ArrowReloadIcon
            className="w-4 h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 cursor-pointer hover:opacity-70 hover:scale-110 transition-all"
            title="Replace Creative"
            onClick={handleReplaceClick}
          />
        ),
      };
    } else {
      return {
        icon: (
          <DeleteIcon
            className="w-4 h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-red-500 cursor-pointer hover:opacity-70 hover:scale-110 transition-all"
            title="Delete Creative"
            onClick={handleDeleteClick}
          />
        ),
      };
    }
  };

  /**
   * Get file format for display
   */
  const getDisplayFormat = () => {
    if (creative.imageUrl?.startsWith("data:")) {
      const match = creative.imageUrl.match(/^data:(image\/[a-zA-Z]+);base64,/);
      return match ? match[1] : "unknown";
    }

    if (creative.fileFormat) {
      return `image/${creative.fileFormat.toLowerCase()}`;
    }

    if (creative.imageUrl) {
      const urlParts = creative.imageUrl.split(".");
      const extension = urlParts[urlParts.length - 1]
        .split("?")[0]
        .toLowerCase();
      return `image/${extension}`;
    }

    return creative.format || "unknown";
  };

  /**
   * Get tags display text
   */
  const getTagsDisplayText = () => {
    if (
      !creative.tags ||
      !Array.isArray(creative.tags) ||
      creative.tags.length === 0
    ) {
      return "All tags selected";
    }

    const validTagCategories = [
      "City",
      "Location",
      "Category",
      "Type",
      "Region",
      "Department",
      "Brand",
      "Product",
      "Service",
      "Event",
    ];

    const valuesToExclude = [
      "Bangalore",
      "Mumbai",
      "Delhi",
      "Chennai",
      "Hyderabad",
      "Pune",
      "Food",
      "Restaurant",
      "Shopping",
      "Entertainment",
      "Sports",
      "Immediate",
      "Later",
      "Scheduled",
      "General",
      "Specific",
    ];

    const cleanTags = creative.tags.filter((tag) => {
      if (!tag || typeof tag !== "string" || tag.trim() === "") {
        return false;
      }

      const trimmedTag = tag.trim();

      if (validTagCategories.includes(trimmedTag)) {
        return true;
      }

      if (valuesToExclude.includes(trimmedTag)) {
        return false;
      }

      if (trimmedTag.length <= 15 && /^[A-Z][a-z]*$/.test(trimmedTag)) {
        return true;
      }

      return false;
    });

    if (cleanTags.length > 0) {
      return cleanTags.join(", ");
    }

    return "All tags selected";
  };

  const MAX_VISIBLE_TAGS = 3;
  const statusInfo = getStatusInfo();
  const tagsDisplayText = getTagsDisplayText();
  const actionButtonConfig = getActionButtonConfig();

  const backgroundOptions = {
    gradient: "bg-gradient-to-br from-blue-50 to-indigo-100",
    solid: "bg-gray-100",
    brandGradient: "bg-gradient-to-br from-[#0BA6C7]/10 to-[#0BA6C7]/20",
    warmGradient: "bg-gradient-to-br from-orange-50 to-pink-50",
    coolGradient: "bg-gradient-to-br from-blue-50 to-purple-50",
  };

  return (
    <>
      <div className="w-full bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-md transition-all duration-200">
        <div
          className={`relative w-full h-[160px] 2xl:h-[190px] 3xl:h-[190px] 4xl:h-[180px] 5xl:h-[210px] ${backgroundOptions.brandGradient} rounded-t-[16px] flex items-center justify-center overflow-hidden`}
        >
          {!imageError ? (
            <img
              src={creative.imageUrl}
              alt={creative.title || "Creative Thumbnail"}
              className="max-w-full max-h-full object-contain transition-opacity duration-300"
              style={{
                filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.1))",
              }}
              onError={handleImageError}
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-400">
              <svg
                className="w-12 h-12 mb-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span className="text-xs">Image not available</span>
            </div>
          )}

          <div className="absolute top-4 right-4 w-10 h-10 bg-white bg-opacity-90 rounded-full flex items-center justify-center shadow-sm hover:bg-opacity-100 transition-all cursor-pointer">
            <ArrowIcon
              width={20}
              height={20}
              className="text-gray-600"
              title="View Details"
              onClick={handlePreviewClick}
            />
          </div>
        </div>

        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <div
                className={`w-fit flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${statusInfo.bgColor} ${statusInfo.textColor}`}
              >
                {statusInfo.icon}
                {statusInfo.text}
              </div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {creative.title || "Untitled Creative"}
                </h3>
                <PenIcon
                  width={14}
                  height={14}
                  className="text-gray-400 cursor-pointer hover:text-gray-600 flex-shrink-0"
                  title="Edit Title"
                  onClick={handleEditClick}
                />
              </div>

              <div className="text-xs text-gray-500">
                Created on: {formatCreatedOn(creative.createdOn)} |{" "}
                {getDisplayFormat()}
              </div>
            </div>
          </div>

          <div className="space-y-2 mb-0.5">
            <div className="flex items-center text-xs text-gray-600 gap-2">
              <CalenderIcon
                width={14}
                height={14}
                className="flex-shrink-0 text-gray-400"
              />
              <span className="truncate font-medium">{creative.dateRange}</span>
            </div>

            <div className="flex items-center text-xs text-gray-600 gap-2">
              <DesktopPotrateIcon
                width={14}
                height={14}
                className="flex-shrink-0 text-gray-400"
              />
              <span className="truncate font-medium">{creative.locations}</span>
            </div>

            <div className="flex items-start text-xs text-gray-600 gap-2">
              <TagIcon
                width={14}
                height={14}
                className="flex-shrink-0 mt-0.5 text-gray-400"
              />
              <div className="flex flex-wrap items-center gap-1 min-w-0">
                <span className="text-gray-700 text-[12px] font-medium">
                  {tagsDisplayText}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center border-t border-gray-100 px-4 py-3">
          <DownloadIcon
            className="w-4 h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 cursor-pointer hover:opacity-70 hover:scale-110 transition-all"
            title="Download"
            onClick={handleDownload}
          />
          {/* Edit Button */}
          <PencilEditIcon
            className="w-4 h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 cursor-pointer hover:opacity-70 hover:scale-110 transition-all"
            title="Edit"
            onClick={handleEditClick}
          />

          {/* Conditional Action Button - Replace or Delete */}
          {actionButtonConfig.icon}

          {/* Play/Pause Toggle Button */}
          <div onClick={handleTogglePlayPause} className="cursor-pointer">
            {creative.isPlaying ? (
              <PauseIcon
                className="w-4 h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-red-500 cursor-pointer hover:opacity-70 hover:scale-110 transition-all"
                title="Pause"
              />
            ) : (
              <PlayIcon
                className="w-4 h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-green-500 cursor-pointer hover:opacity-70 hover:scale-110 transition-all"
                title="Play"
              />
            )}
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} />

      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
          <ScheduledCreativePreview
            creative={creative}
            onClose={() => setShowPreview(false)}
          />
        </div>
      )}

      {isEditing && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-40">
          <ItemListPopup
            title="Edit Creative"
            onClose={() => {
              setIsEditing(false);
              setEditedTitle(creative.title);
              setTitleError("");
            }}
          >
            <div className="flex flex-col h-full max-h-[90vh]">
              <div className="flex justify-between items-center mb-4">
                <span className="text-sm font-medium text-gray-700">
                  Dimension
                </span>
                <span className="text-sm text-gray-600">
                  {getDimensions(creative.orientation)}
                </span>
              </div>

              <div className="mb-4">
                <div className="w-full flex justify-center">
                  {!imageError ? (
                    <img
                      src={creative.imageUrl}
                      alt={creative.title || "Creative Preview"}
                      className={`h-auto rounded-lg border-4 border-[#A6A6A6] ${
                        creative.orientation === "portrait" ? "w-1/2" : "w-full"
                      }`}
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center text-gray-400">
                      <svg
                        className="w-12 h-12 mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="text-sm">Image not available</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Creative name
                </label>
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  className={`w-full border rounded-[8px] px-3 py-2 ${
                    titleError
                      ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                      : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                  } focus:outline-none focus:ring-2`}
                  maxLength={32}
                  placeholder="Enter creative name"
                />
                {titleError && (
                  <p className="mt-1 text-sm text-red-600">{titleError}</p>
                )}
              </div>

              <div className="mt-auto">
                <hr className="border-t border-gray-200 mt-1" />
                <div className="flex justify-end gap-3 pt-4">
                  <CustomButton
                    variant="outline"
                    onClick={() => {
                      setIsEditing(false);
                      setEditedTitle(creative.title);
                      setTitleError("");
                    }}
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton
                    isActive={
                      editedTitle !== creative.title &&
                      !titleError &&
                      !!editedTitle.trim()
                    }
                    onClick={handleTitleUpdate}
                    disabled={!!titleError || !editedTitle.trim()}
                  >
                    Apply changes
                  </CustomButton>
                </div>
              </div>
            </div>
          </ItemListPopup>
        </div>
      )}

      {showActionConfirm && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <DeletePopup
            title={
              actionType === "pause" ? "Deactivate Creative" : "Delete Creative"
            }
            message={
              actionType === "pause"
                ? "Are you sure you want to deactivate this creative? It will stop playing on all screens."
                : "Are you sure you want to delete this creative? This action cannot be undone."
            }
            onCancel={() => setShowActionConfirm(false)}
            onConfirm={handleConfirmAction}
          />
        </div>
      )}
    </>
  );
};

export default ScheduledCreativesCard;
